import styles from '../templates/styles.css?inline';
import addressBookHtml from '../templates/address-book-modal.html?raw';

const ICONS = {
  search: `<svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>`,
  check: `<svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>`,
  mapPin: `<svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>`,
  phone: `<svg viewBox="0 0 24 24" width="12" height="12" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>`,
  mail: `<svg viewBox="0 0 24 24" width="12" height="12" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>`
};

export class AddressBookModal {
  constructor(sdk, hostElement) {
    this.sdk = sdk;
    this.host = hostElement;
    this.visible = false;
    this.callback = null;

    this.rowId = null;
    this.addressType = 'SENDER'; // 'SENDER' | 'RECIPIENT'
    this.searchTerm = '';
    this.debounceTimeout = null;
    this.addresses = [];
    this.isLoading = false;

    // Initialize Shadow DOM
    this.shadowRoot = this.host.attachShadow({ mode: 'open' });
    
    // Ensure correct stacking context
    this.host.style.position = 'relative';
    this.host.style.zIndex = '9999999998';

    this.init();
    this.sdk.registerAddressBookModal(this);
  }

  init() {
    // Append styles
    const styleEl = document.createElement('style');
    styleEl.textContent = styles;
    this.shadowRoot.appendChild(styleEl);

    // Create container wrapper
    this.container = document.createElement('div');
    this.shadowRoot.appendChild(this.container);

    this.render();
  }

  show(rowId, rowType, callback) {
    this.visible = true;
    this.rowId = rowId;
    this.addressType = rowType === 'SENDER' || rowType === 'SHIPPER' || rowType === 'origin' || rowType === 'Pickup' ? 'SENDER' : 'RECIPIENT';
    this.searchTerm = '';
    this.callback = callback;
    this.isLoading = false;
    this.addresses = [];

    if (!document.body.dataset.adbScrollLocked) {
      document.body.dataset.adbScrollLocked = 'true';
      this._prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }

    this.render();
    this.fetchAddresses();
  }

  hide() {
    this.visible = false;
    if (document.body.dataset.adbScrollLocked) {
      delete document.body.dataset.adbScrollLocked;
      document.body.style.overflow = this._prevOverflow || '';
    }
    this.render();
    this.callback = null;
  }

  getTemplate(templateId) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(addressBookHtml, 'text/html');
    const template = doc.getElementById(templateId);
    return template ? template.innerHTML : '';
  }

  injectIcons(htmlString) {
    return htmlString.replace(/__ICON_SEARCH__/g, ICONS.search);
  }

  async fetchAddresses() {
    this.isLoading = true;
    this.showStatus('loading');
    this.renderList();

    try {
      const payload = {
        merchantId: 1, // Default merchant context
        addressType: this.addressType,
        searchTerm: this.searchTerm
      };

      const response = await this.sdk.getAddressBook(payload);

      if (response && response.isSuccessful && response.data && response.data.result) {
        this.addresses = response.data.result;
        this.isLoading = false;

        if (this.addresses.length === 0) {
          this.showStatus('empty');
        } else {
          this.showStatus('list');
        }
      } else {
        throw new Error(response?.resultMessage || "Failed to fetch addresses");
      }
    } catch (err) {
      console.error("[AddressBookModal] Error loading addresses:", err);
      this.isLoading = false;
      this.showStatus('error');
    }

    this.renderList();
  }

  showStatus(status) {
    const listEl = this.shadowRoot.getElementById('address-book-list');
    const loadingEl = this.shadowRoot.getElementById('address-book-loading');
    const emptyEl = this.shadowRoot.getElementById('address-book-empty');
    const errorEl = this.shadowRoot.getElementById('address-book-error');

    if (!listEl) return;

    listEl.style.display = status === 'list' ? 'block' : 'none';
    loadingEl.style.display = status === 'loading' ? 'flex' : 'none';
    emptyEl.style.display = status === 'empty' ? 'flex' : 'none';
    errorEl.style.display = status === 'error' ? 'flex' : 'none';
  }

  renderList() {
    const listEl = this.shadowRoot.getElementById('address-book-list');
    if (!listEl) return;

    listEl.innerHTML = '';
    if (this.isLoading || this.addresses.length === 0) return;

    this.addresses.forEach(item => {
      const card = document.createElement('div');
      card.className = 'address-book-item-card';
      
      const headerRow = document.createElement('div');
      headerRow.className = 'address-item-header';

      const title = document.createElement('div');
      title.className = 'address-item-name';
      title.textContent = item.name || 'Unnamed Address';

      const badges = document.createElement('div');
      badges.className = 'address-item-badges';

      if (item.isDefault) {
        const defaultBadge = document.createElement('span');
        defaultBadge.className = 'address-badge default';
        defaultBadge.textContent = 'Default';
        badges.appendChild(defaultBadge);
      }
      if (item.isVerified) {
        const verifiedBadge = document.createElement('span');
        verifiedBadge.className = 'address-badge verified';
        verifiedBadge.innerHTML = `${ICONS.check} Verified`;
        badges.appendChild(verifiedBadge);
      }

      headerRow.appendChild(title);
      headerRow.appendChild(badges);

      const addressRow = document.createElement('div');
      addressRow.className = 'address-item-details';
      addressRow.innerHTML = `${ICONS.mapPin} <span>${item.street1}${item.street2 ? ', ' + item.street2 : ''}, ${item.city}, ${item.state} ${item.postalCode}, ${item.countryCode}</span>`;

      const contactRow = document.createElement('div');
      contactRow.className = 'address-item-contacts';
      
      if (item.email) {
        const emailSpan = document.createElement('span');
        emailSpan.innerHTML = `${ICONS.mail} <span>${item.email}</span>`;
        contactRow.appendChild(emailSpan);
      }
      if (item.phoneNumber) {
        const phoneSpan = document.createElement('span');
        phoneSpan.innerHTML = `${ICONS.phone} <span>${item.phoneNumber}</span>`;
        contactRow.appendChild(phoneSpan);
      }

      card.appendChild(headerRow);
      card.appendChild(addressRow);
      if (contactRow.children.length > 0) {
        card.appendChild(contactRow);
      }

      // Add selection click handler
      card.addEventListener('click', () => {
        if (this.callback) {
          this.callback(item);
        }
        this.hide();
      });

      listEl.appendChild(card);
    });
  }

  handleSearchInput(e) {
    this.searchTerm = e.target.value;
    
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }

    this.debounceTimeout = setTimeout(() => {
      this.fetchAddresses();
    }, 300);
  }

  render() {
    if (!this.visible) {
      this.container.innerHTML = '';
      return;
    }

    // Set structure
    const rawTemplate = this.getTemplate('address-book-modal-template');
    this.container.innerHTML = this.injectIcons(rawTemplate);

    // Bind Close Click
    const btnClose = this.shadowRoot.getElementById('btn-close-address-book');
    if (btnClose) {
      btnClose.addEventListener('click', () => this.hide());
    }

    // Bind Search Input
    const searchInput = this.shadowRoot.getElementById('address-book-search-input');
    if (searchInput) {
      searchInput.value = this.searchTerm;
      searchInput.addEventListener('input', this.handleSearchInput.bind(this));
    }

    // Setup active tab highlights
    const tabShipper = this.shadowRoot.getElementById('tab-shipper');
    const tabRecipient = this.shadowRoot.getElementById('tab-recipient');

    if (tabShipper && tabRecipient) {
      if (this.addressType === 'SENDER') {
        tabShipper.classList.add('active');
        tabRecipient.classList.remove('active');
      } else {
        tabRecipient.classList.add('active');
        tabShipper.classList.remove('active');
      }

      tabShipper.addEventListener('click', () => {
        if (this.addressType === 'SENDER') return;
        this.addressType = 'SENDER';
        this.fetchAddresses();
        this.render();
      });

      tabRecipient.addEventListener('click', () => {
        if (this.addressType === 'RECIPIENT') return;
        this.addressType = 'RECIPIENT';
        this.fetchAddresses();
        this.render();
      });
    }
  }
}
